// src/context/AuthContext.tsx

import React, { createContext, useContext, useEffect, useReducer } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useUserStore } from '../store/useUserStore';
import type { User } from '../types/user'; // ✅ Ensure this is importing 'User'
import type { ILoginResponse } from '../types/auth'; // Ensure this is imported for LoginSuccessAction

// Define the shape of our context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginUser: (email: string, password: string) => Promise<void>;
  logoutUser: () => Promise<void>;
  loadUser: () => Promise<void>;
}

type AuthState = {
  user: User | null;
  loading: boolean;
};

// Payload for LoginSuccessAction correctly expects a 'User' object
type LoginSuccessAction = { type: 'LOGIN_SUCCESS'; payload: User };
// Payload for LoadUserAction correctly expects a 'User' or null
type LoadUserAction = { type: 'LOAD_USER'; payload: User | null };
type LogoutAction = { type: 'LOGOUT' };
type SetLoadingAction = { type: 'SET_LOADING'; payload: boolean };

type AuthAction = LoginSuccessAction | LoadUserAction | LogoutAction | SetLoadingAction;

const initialState: AuthState = {
  user: null,
  loading: true,
};

const AuthContext = createContext<AuthContextType | null>(null);

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      // action.payload is of type User, which matches state.user
      return { ...state, user: action.payload, loading: false };
    case 'LOAD_USER':
      // action.payload is of type User | null, which matches state.user
      return { ...state, user: action.payload, loading: false };
    case 'LOGOUT':
      return { ...state, user: null, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { setUser, clearUser } = useUserStore();
  const navigate = useNavigate();

  const loadUser = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = await authService.getCurrentUser(); // This returns User | null
      dispatch({ type: 'LOAD_USER', payload: user });
      if (user) {
        setUser(user);
      } else {
        clearUser();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      dispatch({ type: 'LOAD_USER', payload: null });
      clearUser();
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const loginUser = async (email: string, password: string) => {
    try {
      // authService.login returns ILoginResponse, whose 'user' property should now be 'User'
      const response: ILoginResponse = await authService.login({ email, password });
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.user });
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logoutUser = async () => {
    await authService.logout();
    dispatch({ type: 'LOGOUT' });
    clearUser();
    navigate('/login');
  };

  const value = {
    user: state.user,
    isAuthenticated: !!state.user,
    isLoading: state.loading,
    loginUser,
    logoutUser,
    loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};